import { RpxResponse, rpx } from 'client/lib/rpx-client';

type CancellationData = RpxResponse<typeof rpx.communityDashboard.getCancellationReasons>;
type ReasonRow = CancellationData['reasons'][number];
type ReasonSummary = CancellationData['summary'][number];

const REASON_LABELS: Record<string, string> = {
  'not-using': "Haven't been using it",
  'too-expensive': 'Too expensive',
  'missing-features': 'Not finding what I need',
  temporary: 'Need a break',
  switching: 'Exploring other options',
  other: 'Something else',
  'pause:not-using': "Paused — haven't been using it",
  'pause:too-expensive': 'Paused — too expensive',
  'pause:missing-features': 'Paused — not finding what I need',
  'pause:temporary': 'Paused — need a break',
  'pause:switching': 'Paused — exploring other options',
  'pause:other': 'Paused — something else',
};

function reasonLabel(reason: string) {
  return REASON_LABELS[reason] || reason;
}

function isPause(reason: string) {
  return reason.startsWith('pause:');
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function SummaryBar({ summary }: { summary: ReasonSummary[] }) {
  const total = summary.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) {
    return null;
  }

  const cancels = summary.filter((s) => !isPause(s.reason));
  const pauses = summary.filter((s) => isPause(s.reason));
  const cancelTotal = cancels.reduce((sum, s) => sum + s.count, 0);
  const pauseTotal = pauses.reduce((sum, s) => sum + s.count, 0);

  return (
    <div class="flex gap-6 mb-6">
      <div class="flex items-center gap-2">
        <span class="text-2xl font-bold text-gray-900">{total}</span>
        <span class="text-sm text-gray-500">total</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="inline-block w-2.5 h-2.5 rounded-full bg-red-400" />
        <span class="text-sm text-gray-600">{cancelTotal} canceled</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="inline-block w-2.5 h-2.5 rounded-full bg-indigo-400" />
        <span class="text-sm text-gray-600">{pauseTotal} paused</span>
      </div>
    </div>
  );
}

function ReasonBreakdown({ summary }: { summary: ReasonSummary[] }) {
  const total = summary.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) {
    return null;
  }

  return (
    <div class="grid grid-cols-2 gap-2 mb-6">
      {summary.map((s) => {
        const pct = ((s.count / total) * 100).toFixed(0);
        return (
          <div key={s.reason} class="flex items-center gap-2">
            <div class="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
              <div
                class={`h-full rounded-full ${isPause(s.reason) ? 'bg-indigo-200' : 'bg-red-200'}`}
                style={`width: ${pct}%`}
              />
            </div>
            <span class="text-xs text-gray-600 w-28 truncate" title={reasonLabel(s.reason)}>
              {reasonLabel(s.reason)}
            </span>
            <span class="text-xs font-medium text-gray-900 w-8 text-right">{s.count}</span>
          </div>
        );
      })}
    </div>
  );
}

function ReasonTable({ reasons }: { reasons: ReasonRow[] }) {
  if (reasons.length === 0) {
    return (
      <p class="text-gray-400 text-sm text-center py-6">
        No cancellation or pause events in this period.
      </p>
    );
  }

  return (
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b text-left text-gray-500 text-xs">
            <th class="pb-2 pr-4 font-medium">Date</th>
            <th class="pb-2 pr-4 font-medium">Member</th>
            <th class="pb-2 pr-4 font-medium">Course</th>
            <th class="pb-2 pr-4 font-medium">Reason</th>
            <th class="pb-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {reasons.map((r) => (
            <tr key={r.id} class="border-b hover:bg-gray-50">
              <td class="py-2 pr-4 text-xs text-gray-600 whitespace-nowrap">
                {formatDate(r.createdAt)}
              </td>
              <td class="py-2 pr-4">
                <div class="text-xs font-medium text-gray-900">{r.userName || r.userEmail}</div>
                {r.userName && <div class="text-xs text-gray-400">{r.userEmail}</div>}
              </td>
              <td class="py-2 pr-4 text-xs text-gray-600 max-w-[160px] truncate">
                {r.courseTitle || '—'}
              </td>
              <td class="py-2 pr-4">
                <span
                  class={`inline-block text-xs px-2 py-0.5 rounded-full ${
                    isPause(r.reason) ? 'bg-indigo-50 text-indigo-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  {reasonLabel(r.reason)}
                </span>
              </td>
              <td class="py-2 text-xs text-gray-500 max-w-[200px] truncate" title={r.notes}>
                {r.notes || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CancellationReasons({ data }: { data: CancellationData }) {
  return (
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Cancellations & Pauses</h3>
      <SummaryBar summary={data.summary} />
      <ReasonBreakdown summary={data.summary} />
      <ReasonTable reasons={data.reasons} />
    </div>
  );
}
